import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@cbrebuild/blocks';

/**
 * Generic comparison table component that works for any data type
 * Configuration determines which columns to show and how to render them
 */
const AdminGenericComparisonTable = ({ data, onBulkUpdate, onCancel, loading, config, toast }) => {
  const [decisions, setDecisions] = useState(() => {
    const initialDecisions = {};
    data.forEach(record => {
      const recordId = record[config.idField];
      initialDecisions[recordId] = ['unchanged', 'invalid'].includes(record.status) ? 'ignore' : 'accept';
    });
    return initialDecisions;
  });

  const [showAllRecords, setShowAllRecords] = useState(false);

  const filteredData = useMemo(() => {
    if (showAllRecords) return data;
    return data.filter(record => record.status !== 'unchanged');
  }, [data, showAllRecords]);

  const handleDecisionChange = (recordId, decision) => {
    setDecisions(prev => ({ ...prev, [recordId]: decision }));
  };

  const handleAcceptAll = () => {
    const newDecisions = {};
    data.forEach(record => {
      const recordId = record[config.idField];
      newDecisions[recordId] = !['unchanged', 'invalid'].includes(record.status) ? 'accept' : 'ignore';
    });
    setDecisions(newDecisions);
  };

  const handleIgnoreAll = () => {
    const newDecisions = {};
    data.forEach(record => {
      newDecisions[record[config.idField]] = 'ignore';
    });
    setDecisions(newDecisions);
  };

  const handleSubmit = () => {
    // Only send records where user made an explicit decision (not default 'ignore')
    // This dramatically reduces payload size - only changed records are sent
    const updates = data
      .filter(record => {
        const decision = decisions[record[config.idField]];
        // Include if user explicitly accepted, or explicitly ignored after it was auto-accepted
        // Skip records with no decision or default 'ignore' state
        return decision && decision !== 'ignore';
      })
      .map(record => {
        // Extract fields from the nested data object
        const recordData = record.data || record.new_data || {};
        
        const update = {
          [config.idField]: record[config.idField],
          action: decisions[record[config.idField]],
          data: recordData
        };
        return update;
      });
    
    // If no records to update, show warning
    if (updates.length === 0) {
      toast('No records selected for update. Please accept at least one record.', 'warning');
      return;
    }
    
    onBulkUpdate(updates);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'updated': return 'admin-pool-comparison-badge-updated';
      case 'new': return 'admin-pool-comparison-badge-new';
      case 'unchanged': return 'admin-pool-comparison-badge-unchanged';
      default: return '';
    }
  };

  const getAcceptTitle = (record, isUnchanged) => {
    if (isUnchanged) return 'No changes to accept';
    if (record.status === 'invalid') return 'Fix validation errors before accepting';
    return 'Accept changes';
  };

  const renderDiscrepancyCell = (field, discrepancies, record) => {
    if (!discrepancies || !discrepancies[field]) {
      const newValue = record.new_data?.[field];
      if (newValue) return <span className="admin-pool-comparison-value">{newValue}</span>;
      return <span className="admin-pool-comparison-no-change">-</span>;
    }

    const { current, new: newValue } = discrepancies[field];
    return (
      <div className="admin-pool-comparison-discrepancy">
        <div className="admin-pool-comparison-old-value">
          <span className="admin-pool-comparison-label">Current:</span> {current || '(empty)'}
        </div>
        <Icon iconName="arrow-right" />
        <div className="admin-pool-comparison-new-value">
          <span className="admin-pool-comparison-label">New:</span> {newValue || '(empty)'}
        </div>
      </div>
    );
  };

  const { acceptCount, ignoreCount } = useMemo(() => {
    const decisionValues = Object.values(decisions);
    return {
      acceptCount: decisionValues.filter(d => d === 'accept').length,
      ignoreCount: decisionValues.filter(d => d === 'ignore').length
    };
  }, [decisions]);

  return (
    <div className="admin-pool-comparison-table-wrapper">
      <div className="admin-pool-comparison-actions-header">
        <div className="admin-pool-comparison-filter-toggle">
          <label>
            <input
              type="checkbox"
              checked={showAllRecords}
              onChange={(e) => setShowAllRecords(e.target.checked)}
            />
            <span>Show unchanged records ({data.length - filteredData.length})</span>
          </label>
        </div>
        
        <div className="admin-pool-comparison-bulk-actions">
          <Button onClick={handleAcceptAll} variant="primary" size="small">
            Accept All
          </Button>
          <Button onClick={handleIgnoreAll} variant="secondary" size="small">
            Ignore All
          </Button>
        </div>
      </div>

      <div className="admin-pool-comparison-decision-summary">
        <span>Accepted: <strong>{acceptCount}</strong></span>
        <span>Ignored: <strong>{ignoreCount}</strong></span>
      </div>

      <div className="admin-pool-comparison-table-container">
        <table className="admin-pool-comparison-table">
          <thead>
            <tr>
              <th className="admin-pool-comparison-col-status">Status</th>
              {config.columns.map(col => (
                <th key={col.field} className={col.className}>
                  {col.label}
                </th>
              ))}
              <th className="admin-pool-comparison-col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={config.columns.length + 2} className="admin-pool-comparison-no-data">
                  No records require action. All records are unchanged.
                </td>
              </tr>
            ) : (
              filteredData.map((record) => {
                const recordId = record[config.idField];
                const decision = decisions[recordId];
                const isUnchanged = record.status === 'unchanged';
                const rowClass = decision === 'accept' ? 'admin-pool-row-accepted' : decision === 'ignore' ? 'admin-pool-row-ignored' : '';

                return (
                  <tr key={recordId} className={rowClass}>
                    <td className="admin-pool-comparison-status-cell">
                      <span className={`admin-pool-comparison-badge ${getStatusBadgeClass(record.status)}`}>
                        {record.status}
                      </span>
                      {record.discrepancies?.errors?.map(error => (
                        <div key={error} className="admin-pool-comparison-error">{error}</div>
                      ))}
                    </td>
                    {config.columns.map(col => (
                      <td key={col.field} className="admin-pool-comparison-field-cell">
                        {col.field === config.idField 
                          ? recordId 
                          : renderDiscrepancyCell(col.field, record.discrepancies, record)
                        }
                      </td>
                    ))}
                    <td className="admin-pool-comparison-action-cell">
                      <div className="admin-pool-comparison-action-buttons">
                        <Button
                          onClick={() => handleDecisionChange(recordId, 'accept')}
                          disabled={isUnchanged || record.status === 'invalid' || loading}
                          variant={decision === 'accept' ? 'primary' : 'secondary'}
                          size="small"
                          title={getAcceptTitle(record, isUnchanged)}
                          className={decision === 'accept' ? 'admin-pool-btn-selected' : ''}
                        >
                          {decision === 'accept' ? '✓ Accept' : 'Accept'}
                        </Button>
                        <Button
                          onClick={() => handleDecisionChange(recordId, 'ignore')}
                          disabled={loading}
                          variant={decision === 'ignore' ? 'danger' : 'secondary'}
                          size="small"
                          title="Ignore changes"
                          className={decision === 'ignore' ? 'admin-pool-btn-selected' : ''}
                        >
                          {decision === 'ignore' ? '✓ Ignore' : 'Ignore'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-pool-comparison-submit-actions">
        <Button onClick={handleSubmit} disabled={loading || acceptCount === 0} variant="primary">
          Apply Changes ({acceptCount})
        </Button>
        <Button onClick={onCancel} disabled={loading} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};

AdminGenericComparisonTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    status: PropTypes.oneOf(['unchanged', 'updated', 'new']).isRequired,
    current_data: PropTypes.object,
    new_data: PropTypes.object.isRequired,
    discrepancies: PropTypes.object.isRequired,
  })).isRequired,
  onBulkUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  toast: PropTypes.func.isRequired,
  config: PropTypes.shape({
    idField: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      className: PropTypes.string,
    })).isRequired,
  }).isRequired,
};

AdminGenericComparisonTable.defaultProps = {
  loading: false,
};

export default AdminGenericComparisonTable;
